import { selectIsAccountAddressReverted } from '@epicuro-next/platform/configuration';
import {
  filterDeletedAddress,
  getFullShipToAddress,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BasketAccountAddress } from 'graphql-api/baskets-api-interfaces';
import { Account } from 'graphql-api/gql-api-interfaces';

import { ExtendedBasketAccountAddress } from '../../types';
import { State as ApplicationState } from '../store';

import { selectBasketAccountsState } from './feature.selectors';

export const selectActiveBasketAccounts = createSelector(
  selectBasketAccountsState,
  (state) => state.activeBasketAccounts,
);

export const selectOpenedBasketAccounts = createSelector(
  selectBasketAccountsState,
  (state) => state.openedBasketAccounts,
);

export const selectActiveBasketAccountsReversed = createSelector(
  selectBasketAccountsState,
  (state) => {
    return [...state.activeBasketAccounts].reverse();
  },
);

export const selectBasketAccounts = createSelector(
  selectOpenedBasketAccounts,
  selectActiveBasketAccounts,
  (openedBasketAccounts, activeBasketAccounts) =>
    openedBasketAccounts.length ? openedBasketAccounts : activeBasketAccounts,
);

export const selectCurrentBasketAccounts = createSelector(
  selectBasketAccounts,
  (accounts) => filterDeletedAddress(accounts),
);

/**
 * Select delivery shops to basket update
 */
export const selectDeliveryShops = createSelector<
  ApplicationState,
  [Account[]],
  BasketAccountAddress[]
>(selectCurrentBasketAccounts, (accounts) => {
  return accountsToBasketAccountsItems(accounts);
});

export const selectDeliveryShopIds = createSelector(
  selectDeliveryShops,
  (shops) => shops.map(({ addressId }) => addressId),
);

export function accountsToBasketAccountsItems(
  accounts: Account[],
): BasketAccountAddress[] {
  return accounts.flatMap((account) =>
    account.shipToAddresses
      .filter((address) => address.hasDeliveryItems)
      .map(({ addressId }) => ({
        addressId,
        accountId: account.accountId,
      })),
  );
}

/**
 * Select delivery shops to basket update
 */
export const selectExtendedDeliveryShops = createSelector<
  ApplicationState,
  [Account[], boolean],
  ExtendedBasketAccountAddress[]
>(
  selectCurrentBasketAccounts,
  selectIsAccountAddressReverted,
  (accounts, isAddressReverted) => {
    return accountsToExtendedBasketAccountsItems(accounts, isAddressReverted);
  },
);

export function accountsToExtendedBasketAccountsItems(
  accounts: Account[],
  revertAddress: boolean,
): ExtendedBasketAccountAddress[] {
  return accounts.flatMap((account) =>
    account.shipToAddresses
      .filter((address) => address.hasDeliveryItems)
      .map((address) => ({
        addressId: address.addressId,
        realAddressId: address.realAddressId,
        accountId: account.accountId,
        externalAccountId: account.externalId,
        name: address.name,
        address: getFullShipToAddress(address, {
          revertAddress,
          countryCodePreferred: false,
        }),
      })),
  );
}
